/**
 * Signed-asset resolver (v3, T-010).
 *
 * The document stores canonical relative paths ("/assets/{user}/{file}"); the
 * backend serves them only via short-lived signed URLs. This util turns a
 * canonical path into a signed URL via POST /api/images/sign-urls, caching the
 * result in memory and refreshing when it is within 5 minutes of expiry.
 */
import axiosInstance from './axios';

// path -> { url, exp } (exp = unix seconds parsed from the signed URL)
const cache = new Map();
// path -> Promise (de-dupe concurrent requests for the same path)
const inflight = new Map();

const REFRESH_MARGIN_SECONDS = 300; // refresh when <5 min to expiry

function parseExp(url) {
  const m = /[?&]exp=(\d+)/.exec(url || '');
  return m ? parseInt(m[1], 10) : 0;
}

function isFresh(entry) {
  if (!entry) return false;
  const now = Math.floor(Date.now() / 1000);
  return entry.exp - now > REFRESH_MARGIN_SECONDS;
}

export function isCanonicalAsset(path) {
  return typeof path === 'string' && path.startsWith('/assets/');
}

/** Drop a cached signature so the next resolve re-signs (used on img error). */
export function invalidateSignedAsset(path) {
  cache.delete(path);
}

/**
 * Synchronous lookup: the still-fresh signed URL if we already have one, else
 * null. Lets a remounting NodeView paint the image on the first frame (no null
 * flash → no dark box, no inline reflow) when the signature survives in cache.
 */
export function getCachedSignedAsset(path) {
  if (!isCanonicalAsset(path)) return path;
  const cached = cache.get(path);
  return isFresh(cached) ? cached.url : null;
}

/**
 * Resolve a canonical /assets path to a signed URL. Non-asset inputs (external
 * URLs, data URLs) are returned unchanged. Returns the original path as a
 * fallback if signing fails.
 */
export async function resolveSignedAsset(path) {
  if (!isCanonicalAsset(path)) return path;

  const cached = cache.get(path);
  if (isFresh(cached)) return cached.url;

  if (inflight.has(path)) return inflight.get(path);

  const request = (async () => {
    try {
      const res = await axiosInstance.post('/images/sign-urls', { paths: [path] });
      const url = res.data?.urls?.[path];
      if (url) {
        cache.set(path, { url, exp: parseExp(url) });
        return url;
      }
      return path;
    } catch (err) {
      console.error('Failed to sign asset', path, err);
      return path;
    } finally {
      inflight.delete(path);
    }
  })();

  inflight.set(path, request);
  return request;
}
